import API from "serviceshift-ui/api-client";

import CONSTANTS from "@/Constants.js";
const { PHOTO_UPLOAD_SOURCE, PHOTO_MAX_SIZE } = CONSTANTS;

export default class PhotoUpload {
  uploadSource: string;
  maxSize: number;
  id: string;
  success: number;
  failures: number;
  photos: Photo[] = [];
  uploadPayloads: UploadPayload[];
  link: {
    href: string;
    method: string;
  };
  onProgress: (progress: ProgressUpdate) => void;
  cancelled = false;
  cancelSource = API.axios.CancelToken.source();
  failedUploads: UploadPayload[] = [];

  constructor(params) {
    this.id = params.id || `${Date.now()}${Math.floor(Math.random() * 100000)}`;
    this.success = params.success || 0;
    this.failures = params.failures || 0;
    this.link = params.link || {};
    this.photos = params.photos || [];
    this.uploadSource = params.uploadSource || PHOTO_UPLOAD_SOURCE;
    this.maxSize = params.maxSize || PHOTO_MAX_SIZE;
  }

  async uploadAll() {
    if (this.link && !this.cancelled) {
      const promises: Promise<void>[] = [];
      this.resetUploader();
      this.uploadPayloads = await this.getUploadPayloads();
      this.uploadPayloads.forEach((payload) =>
        promises.push(this.upload(payload))
      );
      await Promise.all(promises);
      await this.savePhotos(this.uploadPayloads);
    }
  }

  async upload(payload: UploadPayload) {
    if (!this.cancelled) {
      if (payload.photo.file.size > this.maxSize) {
        this.logFailure(payload);
        return;
      }
      // Generate POST body formdata
      // includes s3 pre-signed url params and image file
      const formData = new FormData();
      Object.keys(payload.parameters).forEach((key) => {
        formData.append(key, payload.parameters[key]);
      });
      formData.append("file", payload.photo.file);
      try {
        await API.makeRequest(payload.server_url, {
          method: "POST",
          data: formData,
          headers: {
            ignoreError: true
          },
          cancelToken: this.cancelSource.token
        });
        payload.success = true;
      } catch (err: any) {
        if (err?.message !== "User cancelled upload") {
          console.error(err);
        }
        this.logFailure(payload);
      }
      payload.attempts = 1;
      this.updateProgress();
    }
  }

  savePhotos(payloads: UploadPayload[]): Promise<(number | void)[]> {
    const uploads: Promise<number | void>[] = [];
    if (!this.cancelled) {
      payloads
        .filter((payload) => payload.success)
        .forEach((payload) => {
          uploads.push(
            API.makeRequest(payload.success_url, {
              method: "POST",
              data: {
                source: this.uploadSource
              },
              headers: {
                ignoreError: true
              },
              cancelToken: this.cancelSource.token
            })
              .then(() => (this.success = this.success + 1))
              .catch(() => this.logFailure(payload))
              .finally(() => this.updateProgress())
          );
        });
    }
    return Promise.all(uploads);
  }

  cancel() {
    this.cancelled = true;
    // Cancels all outstanding web requests
    if (this.cancelSource) {
      this.cancelSource.cancel("User cancelled upload");
    }
  }

  get extensions(): string[] {
    return this.photos.map((photo) =>
      photo.fileName.substring(
        photo.fileName.lastIndexOf(".") + 1,
        photo.fileName.length
      )
    );
  }

  get size() {
    return this.photos.length;
  }

  private async getUploadPayloads() {
    if (this.cancelled) return [];
    const res = await API.makeRequest(this.link.href, {
      method: this.link.method,
      data: { extensions: this.extensions }
    });
    return res.data.map((payload, i) => ({
      ...payload,
      photo: this.photos[i]
    }));
  }

  private logFailure(payload: UploadPayload) {
    this.failures = this.failures + 1;
    payload.success = false;
    this.failedUploads.push(payload);
  }

  private resetUploader() {
    this.cancelled = false;
    this.failedUploads = [];
    this.success = 0;
    this.failures = 0;
    this.uploadPayloads = [];
  }

  private updateProgress() {
    if (this.onProgress) {
      const completed =
        this.uploadPayloads.filter((payload) => payload.attempts > 0).length +
        this.success +
        this.failures;
      this.onProgress({
        progress: completed / (this.uploadPayloads.length * 2),
        uploads: this.uploadPayloads
      });
    }
  }
}

export class Photo {
  file: File;
  fileName: string;
}

export class UploadPayload {
  server_url: string;
  success_url: string;
  parameters: {
    key: string;
    policy: string;
    "x-amz-credential": string;
    "x-amz-algorithm": string;
    "x-amz-date": string;
    "x-amz-signature": string;
  };
  curl: string;
  photo: Photo;
  success: boolean;
  attempts: number;
}

interface ProgressUpdate {
  progress: number;
  uploads: UploadPayload[];
}
